/**
 * Dashboard Operacional - Visão Principal Simplificada
 */

import React, { useState, useEffect } from 'react';
import DashboardLayout from './DashboardLayout';
import useEstatisticasDiarias, { PERIODOS, PERIODOS_LABELS } from '../../hooks/useEstatisticasDiarias';
import { CORES } from '../../utils/colors';
import { formatNumber, formatDecimal } from '../../utils/formatters';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import './DashboardOperacional.css';

// Configuração dos KPIs
const KPI_CONFIG = [
  { key: 'total_pessoas', label: 'Pessoas', icone: '👥', cor: CORES.pessoas, formato: 'inteiro' },
  { key: 'total_refeicoes', label: 'Refeições', icone: '🍽️', cor: CORES.refeicoes, formato: 'inteiro' },
  { key: 'total_rondas', label: 'Rondas', icone: '🚐', cor: CORES.rondas || '#6366f1', formato: 'inteiro' },
  { key: 'total_voluntarios', label: 'Voluntários', icone: '🙋', cor: CORES.voluntarios, formato: 'inteiro' },
  { key: 'total_km', label: 'Km', icone: '🛣️', cor: CORES.km, formato: 'decimal' },
  { key: 'kms_voluntario', label: 'Km-Vol', icone: '🚗', cor: '#0ea5e9', formato: 'decimal' },
  { key: 'total_horas', label: 'Horas', icone: '⏱️', cor: CORES.horas, formato: 'decimal' },
  { key: 'horas_voluntario', label: 'H-Vol', icone: '⏰', cor: '#8b5cf6', formato: 'decimal' },
  { key: 'total_paragens_efetivas', label: 'Efetivas', icone: '✅', cor: '#10b981', formato: 'inteiro' }
];

function DashboardOperacional() {
  const {
    loading,
    error,
    periodo,
    entidadeSelecionada,
    totais,
    dadosGrafico,
    entidadesDisponiveis,
    mudarPeriodo,
    mudarEntidade
  } = useEstatisticasDiarias();

  const [metricaSelecionada, setMetricaSelecionada] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Detectar mudança de tamanho da janela
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Formatar valor baseado no tipo
  const formatarValor = (valor, formato) => {
    if (formato === 'decimal') return formatDecimal(valor);
    return formatNumber(valor);
  };

  // Obter dados para o gráfico da métrica selecionada
  const dadosParaGrafico = dadosGrafico?.dados?.map(item => ({
    periodo: dadosGrafico.agrupamento === 'semana'
      ? `${new Date(item.periodo).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })}`
      : new Date(item.periodo).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' }),
    valor: item[metricaSelecionada] || 0
  })) || [];

  // Encontrar config da métrica selecionada
  const metricaConfig = KPI_CONFIG.find(k => k.key === metricaSelecionada);

  if (loading && !totais) {
    return (
      <DashboardLayout titulo="Dashboard Operacional">
        <div className="dashboard-loading">
          <div className="dashboard-loading-spinner"></div>
          <span>Carregando dados...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout titulo="Dashboard Operacional">
        <div className="dashboard-error">
          <span>❌</span>
          <span>{error}</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout titulo="Dashboard Operacional">
      {/* Filtros */}
      <div className="dashboard-filtros">
        <div className="filtro-grupo">
          <label>Entidade:</label>
          <select 
            value={entidadeSelecionada || ''} 
            onChange={(e) => mudarEntidade(e.target.value || null)}
          >
            <option value="">Todas</option>
            {entidadesDisponiveis.map(ent => (
              <option key={ent.id} value={ent.id}>{ent.nome}</option>
            ))}
          </select>
        </div>
        <div className="filtro-grupo">
          <label>Período:</label>
          <select 
            value={periodo} 
            onChange={(e) => mudarPeriodo(e.target.value)}
          >
            {Object.entries(PERIODOS_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="dashboard-kpis">
        {KPI_CONFIG.map(kpi => (
          <div 
            key={kpi.key}
            className={`kpi-card ${metricaSelecionada === kpi.key ? 'kpi-card-active' : ''}`}
            style={{ borderColor: metricaSelecionada === kpi.key ? kpi.cor : 'transparent' }}
            onClick={() => setMetricaSelecionada(metricaSelecionada === kpi.key ? null : kpi.key)}
          >
            <div className="kpi-icone">{kpi.icone}</div>
            <div className="kpi-valor" style={{ color: kpi.cor }}>
              {formatarValor(totais?.[kpi.key] || 0, kpi.formato)}
            </div>
            <div className="kpi-label">{kpi.label}</div>
          </div>
        ))}
      </div>

      {/* Gráfico (aparece quando uma métrica é selecionada) */}
      {metricaSelecionada && metricaConfig && (
        <div className="dashboard-grafico">
          <h3 className="grafico-titulo">
            {metricaConfig.icone} {metricaConfig.label} 
            <span className="grafico-subtitulo">
              ({dadosGrafico.agrupamento === 'semana' ? 'por semana' : 'por dia'})
            </span>
          </h3>
          <div className="grafico-container">
            <ResponsiveContainer width="100%" height={isMobile ? Math.max(200, dadosParaGrafico.length * 25) : 300}>
              <BarChart 
                data={dadosParaGrafico} 
                layout={isMobile ? 'vertical' : 'horizontal'}
                margin={{ top: 10, right: 30, left: isMobile ? 50 : 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                {isMobile ? (
                  <>
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis 
                      type="category" 
                      dataKey="periodo" 
                      tick={{ fontSize: 10 }}
                      width={45}
                    />
                  </>
                ) : (
                  <>
                    <XAxis 
                      dataKey="periodo" 
                      tick={{ fontSize: 12 }}
                      interval={dadosParaGrafico.length > 15 ? Math.floor(dadosParaGrafico.length / 10) : 0}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                  </>
                )}
                <Tooltip 
                  formatter={(value) => [formatarValor(value, metricaConfig.formato), metricaConfig.label]}
                  labelFormatter={(label) => `Data: ${label}`}
                />
                <Bar dataKey="valor" fill={metricaConfig.cor} radius={isMobile ? [0, 4, 4, 0] : [4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Mensagem quando nenhuma métrica selecionada */}
      {!metricaSelecionada && (
        <div className="dashboard-hint">
          <p>👆 Clique num cartão para ver o gráfico de evolução</p>
        </div>
      )}
    </DashboardLayout>
  );
}

export default DashboardOperacional;
